import { useState } from 'react';
import { format } from 'date-fns';
import {
  EyeIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { BuildingOfficeIcon } from '@heroicons/react/24/solid';
import type { NIBSubmission } from '@/types';
import { NIB_FORMAT_LABELS } from '@/types';
import { NIBStatusBadge } from './NIBStatusBadge';
import { EmptyState } from '@components/common/EmptyState';

interface SubmissionListProps {
  submissions: NIBSubmission[];
  isLoading: boolean;
  onView: (submission: NIBSubmission) => void;
  onDelete: (submission: NIBSubmission) => void;
}

type SortField = 'period' | 'employer_name' | 'status' | 'total_records';
type SortOrder = 'asc' | 'desc';

const SortIcon = ({
  field,
  sortField,
  sortOrder,
}: {
  field: SortField;
  sortField: SortField;
  sortOrder: SortOrder;
}) => {
  if (sortField !== field) {
    return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
  }
  return sortOrder === 'asc' ? (
    <ChevronUpIcon className="h-4 w-4 text-blue-600" />
  ) : (
    <ChevronDownIcon className="h-4 w-4 text-blue-600" />
  );
};

export function SubmissionList({
  submissions,
  isLoading,
  onView,
  onDelete,
}: SubmissionListProps) {
  const [sortField, setSortField] = useState<SortField>('period');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedSubmissions = [...submissions].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    switch (sortField) {
      case 'period':
        // Sort by period_year and period_month
        aVal = a.period_year * 100 + a.period_month;
        bVal = b.period_year * 100 + b.period_month;
        break;
      case 'employer_name':
        aVal = a.employer_name.toLowerCase();
        bVal = b.employer_name.toLowerCase();
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      case 'total_records':
        aVal = a.total_records;
        bVal = b.total_records;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const formatPeriod = (month: number, year: number): string => {
    const date = new Date(year, month - 1, 1);
    return format(date, 'MMMM yyyy');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-BS', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleCopyId = async (id: string | number) => {
    try {
      const idStr = String(id);
      await navigator.clipboard.writeText(idStr);
      setCopiedId(idStr);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-sm text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <EmptyState
          icon={
            <svg
              className="h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          title="No submissions found"
          description="No NIB C10 submissions match your current filters."
        />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('period')}
              >
                <div className="flex items-center gap-1">
                  <span>Period</span>
                  <SortIcon
                    field="period"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Submission ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('employer_name')}
              >
                <div className="flex items-center gap-1">
                  <span>Employer</span>
                  <SortIcon
                    field="employer_name"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Format Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  <span>Status</span>
                  <SortIcon
                    field="status"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('total_records')}
              >
                <div className="flex items-center gap-1">
                  <span>Records</span>
                  <SortIcon
                    field="total_records"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Total Contributions
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedSubmissions.map(submission => (
              <tr
                key={submission.id}
                className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                onClick={() => onView(submission)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {formatPeriod(
                        submission.period_month,
                        submission.period_year
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      {submission.contribution_period}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div
                    className="flex items-center gap-2 max-w-[150px]"
                    onClick={e => e.stopPropagation()}
                  >
                    <span
                      className="font-mono text-xs text-gray-600 truncate"
                      title={String(submission.id)}
                    >
                      {String(submission.id).substring(0, 8)}...
                    </span>
                    <button
                      onClick={() => handleCopyId(submission.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy full ID"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                    {copiedId === String(submission.id) && (
                      <span className="text-xs text-green-600">Copied!</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 truncate">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span
                        className="font-medium truncate"
                        title={submission.employer_name || 'Unknown'}
                      >
                        {submission.employer_name || 'Unknown'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 ml-6">
                      #{submission.employer_number}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2">
                    {submission.format_type === 'HOSPITALITY' ? (
                      <BriefcaseIcon className="h-4 w-4 text-blue-600" />
                    ) : (
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-600" />
                    )}
                    <span className="text-xs text-gray-600">
                      {NIB_FORMAT_LABELS[submission.format_type]}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <NIBStatusBadge status={submission.status} size="sm" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="font-medium">
                    {submission.total_records.toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  <div className="flex flex-col items-end">
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(submission.total_contributions)}
                    </span>
                    <span className="text-xs text-gray-500">
                      Insurable:{' '}
                      {formatCurrency(submission.total_insurable_wages)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div
                    className="flex items-center justify-end gap-2"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => onView(submission)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                      title="View details"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onDelete(submission)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="Delete submission"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
